// Theme hook for dark/light mode
import { useState, useEffect, useCallback, useRef } from 'react';
import { storage } from '../services/storage';

type Theme = 'light' | 'dark' | 'system';

const THEME_KEY = 'shopman-theme';

// Helper to resolve theme to light/dark
function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

// Helper to apply theme to DOM (no state updates)
function applyThemeToDOM(resolved: 'light' | 'dark') {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);

  // Update theme-color meta tag
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', resolved === 'dark' ? '#111827' : '#22c55e');
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    return (storage.get(THEME_KEY) as Theme) || 'system';
  });

  // Initialize resolved theme from current theme
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => resolveTheme(
    (storage.get(THEME_KEY) as Theme) || 'system'
  ));

  // Track if initial DOM application has been done
  const initialApplied = useRef(false);

  // Set theme
  const setTheme = useCallback((newTheme: Theme) => {
    const resolved = resolveTheme(newTheme);
    setThemeState(newTheme);
    setResolvedTheme(resolved);
    storage.set(THEME_KEY, newTheme);
    applyThemeToDOM(resolved);
  }, []);

  // Apply theme to DOM on mount and listen for system changes
  useEffect(() => {
    // Apply initial theme to DOM (only once)
    if (!initialApplied.current) {
      applyThemeToDOM(resolvedTheme);
      initialApplied.current = true;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        const newResolved = resolveTheme('system');
        setResolvedTheme(newResolved);
        applyThemeToDOM(newResolved);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, resolvedTheme]);

  return {
    theme,
    resolvedTheme,
    setTheme,
    isDark: resolvedTheme === 'dark',
  };
}

export default useTheme;
